import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./Shared";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { useToast } from "../hooks/use-toast";
import { fetchPlans } from "../lib/api";
import { CUSTOMIZATION_OPTIONS } from "../mock";
import { setCheckoutDraft } from "../lib/mockSub";
import { isAfterCutoff, formatCutoffDisplay } from "../lib/utils";
import { Check, Sparkles, Info, MapPin, X } from "lucide-react";

// ============================================
// PLAN CARD - Premium selection card (Fixed height, no overflow)
// ============================================
function PlanCard({ plan, vendor, isSelected, isPopular, onSelect }) {
  const pricePerMeal = vendor?.plan_prices?.[plan.id];
  const weeklyEstimate = pricePerMeal ? (pricePerMeal * plan.meals).toFixed(2) : null;
  
  return (
    <button
      onClick={onSelect}
      className={`
        relative flex-shrink-0 w-[120px] md:w-full rounded-2xl border-2 transition-all duration-300 ease-out overflow-hidden
        h-[120px] md:h-[140px] flex flex-col justify-center items-center p-3 md:p-4 text-center
        ${isSelected 
          ? 'border-[#2a7d4f] bg-[#2a7d4f] text-white shadow-xl scale-[1.02] md:scale-[1.03]'
          : isPopular
            ? 'border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50/50 hover:shadow-lg hover:scale-[1.02] hover:border-amber-400'
            : 'border-neutral-100 bg-white hover:shadow-lg hover:scale-[1.02] hover:border-neutral-200'
        }
      `}
      data-testid={`plan-card-${plan.meals}`}
    >
      {/* Popular Badge */}
      {isPopular && !isSelected && (
        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2">
          <Badge className="bg-gradient-to-r from-amber-500 to-amber-400 text-white text-[9px] px-2 py-0.5 shadow-md border-0">
            <Sparkles size={9} className="mr-0.5" />
            Popular
          </Badge>
        </div>
      )}
      
      {/* Selected Indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm">
          <Check size={12} className="text-black" />
        </div>
      )}
      
      {/* Meals Count */}
      <div className={`text-2xl md:text-3xl font-bold tracking-tight ${isSelected ? 'text-white' : 'text-black'}`}>
        {plan.meals}
      </div>
      <div className={`text-[10px] md:text-xs font-medium ${isSelected ? 'text-white/70' : 'text-neutral-400'}`}>
        meals/week
      </div>
      
      {/* Price */}
      <div className={`mt-1.5 md:mt-2 ${isSelected ? 'text-white' : 'text-black'}`}>
        <span className="text-lg md:text-xl font-bold">${pricePerMeal?.toFixed(2) || '—'}</span>
        <span className={`text-[10px] md:text-xs ${isSelected ? 'text-white/60' : 'text-neutral-400'}`}>/meal</span>
      </div>
      
      {/* Weekly Total (shown when selected) - compact */}
      {isSelected && weeklyEstimate && (
        <div className="mt-1 pt-1 border-t border-white/20 w-full">
          <div className="text-[9px] md:text-[10px] text-white/60">Weekly</div>
          <div className="text-sm font-bold text-white">${weeklyEstimate}</div>
        </div>
      )}
    </button>
  );
}

// ============================================
// MEAL CARD - Image-first premium card (Fixed text overflow)
// ============================================
function MealCard({ item, quantity, onIncrease, onDecrease }) {
  const hasQuantity = quantity > 0;
  
  return (
    <div 
      className={`
        group relative rounded-2xl overflow-hidden bg-white transition-all duration-300
        ${hasQuantity 
          ? 'ring-2 ring-black shadow-lg' 
          : 'border border-neutral-100 hover:shadow-xl hover:scale-[1.01]'
        }
      `}
    >
      {/* Image - Maintains image-first design */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={item.image} 
          alt={item.name} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
        />
        
        {/* Quantity Badge */}
        {hasQuantity && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#2a7d4f] text-white flex items-center justify-center font-bold text-sm shadow-lg">
            {quantity}
          </div>
        )}
        
        {/* Calories Badge */}
        {item.calories && (
          <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-medium text-neutral-700 shadow-sm">
            {item.calories} cal
          </div>
        )}
      </div>
      
      {/* Content - Fixed overflow with min-w-0 */}
      <div className="p-4 overflow-hidden">
        {/* Title - truncate single line */}
        <h3 className="font-semibold text-base text-neutral-900 leading-snug line-clamp-2">
          {item.name}
        </h3>
        
        {/* Description - max 2 lines with line-clamp */}
        <p className="mt-1.5 text-[13px] leading-relaxed text-neutral-500 line-clamp-2 min-h-[2.5rem]">
          {item.ingredients || item.desc || 'Fresh, quality ingredients'}
        </p>
        
        {/* Meta info - truncate */}
        {item.macros && (
          <div className="mt-2 text-[12px] text-neutral-400 truncate">
            {item.macros.protein_g && `${item.macros.protein_g}g protein`}
            {item.macros.carbs_g && ` · ${item.macros.carbs_g}g carbs`}
          </div>
        )}
        
        {/* Quantity Controls */}
        <div className="mt-3 flex items-center">
          <div className="flex items-center gap-1">
            <button
              onClick={onDecrease}
              disabled={quantity === 0}
              className={`
                w-9 h-9 rounded-xl flex items-center justify-center text-lg font-medium transition-all
                ${quantity > 0 
                  ? 'bg-neutral-100 hover:bg-neutral-200 text-neutral-900' 
                  : 'bg-neutral-50 text-neutral-300 cursor-not-allowed'
                }
              `}
            >
              −
            </button>
            <div className="w-9 h-9 flex items-center justify-center font-semibold text-neutral-900">
              {quantity}
            </div>
            <button
              onClick={onIncrease}
              className="w-9 h-9 rounded-xl bg-[#2a7d4f] hover:bg-[#236840] text-white flex items-center justify-center text-lg font-medium transition-all hover:scale-105"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PROGRESS BAR - Visual meal selection progress
// ============================================
function SelectionProgress({ current, required }) {
  const percentage = Math.min((current / required) * 100, 100);
  const isComplete = current === required;
  const isOver = current > required;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-600">
          {isComplete ? 'Selection complete!' : isOver ? 'Too many selected' : 'Choose your meals'}
        </span>
        <span className={`text-sm font-bold ${isComplete ? 'text-green-600' : isOver ? 'text-amber-600' : 'text-neutral-900'}`}>
          {current} of {required}
        </span>
      </div>
      
      <div className="relative h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div 
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out ${
            isComplete ? 'bg-green-500' : isOver ? 'bg-amber-500' : 'bg-[#2a7d4f]'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {isComplete && (
        <div className="flex items-center gap-1.5 text-green-600 text-sm">
          <Check size={14} />
          <span>Your box is ready</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// ORDER SUMMARY - Sticky summary component
// ============================================
function OrderSummary({ 
  planId, 
  mealsRequired, 
  totalSelected, 
  perMealPrice, 
  modifiersPerMeal,
  isPilot,
  vendor,
  onCheckout,
  isValid 
}) {
  const subtotal = perMealPrice ? perMealPrice * mealsRequired : 0;
  const modifiersTotal = modifiersPerMeal * mealsRequired;
  const total = subtotal + modifiersTotal;
  
  const buttonText = !planId 
    ? 'Select a plan' 
    : totalSelected < mealsRequired 
      ? `Add ${mealsRequired - totalSelected} more meal${mealsRequired - totalSelected > 1 ? 's' : ''}`
      : totalSelected > mealsRequired
        ? 'Remove extra meals'
        : 'Continue to checkout';
  
  return (
    <div className="bg-white rounded-2xl border border-neutral-100 shadow-lg p-6 space-y-5">
      <h3 className="font-semibold text-lg text-neutral-900">Order Summary</h3>
      
      {/* Pickup Info (Pilot) */}
      {isPilot && (
        <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
          <MapPin size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-amber-900">Pickup Only</div>
            <div className="text-xs text-amber-700 mt-0.5">
              {(vendor?.pickup_days || []).join(', ') || 'Days TBD'} • Austin, TX
            </div>
          </div>
        </div>
      )}
      
      {/* Price Breakdown */}
      {perMealPrice && (
        <div className="space-y-3 text-sm">
          <div className="flex justify-between text-neutral-600">
            <span>{mealsRequired} meals × ${perMealPrice.toFixed(2)}</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          
          {modifiersTotal > 0 && (
            <div className="flex justify-between text-neutral-600">
              <span>Add-ons</span>
              <span>${modifiersTotal.toFixed(2)}</span>
            </div>
          )}
          
          <div className="pt-3 border-t border-neutral-100 flex justify-between font-semibold text-neutral-900 text-base">
            <span>Weekly total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      )}
      
      {/* Checkout Button */}
      <Button
        onClick={onCheckout}
        disabled={!isValid}
        className={`
          w-full h-14 rounded-xl text-base font-semibold transition-all duration-300
          ${isValid 
            ? 'bg-[#2a7d4f] hover:bg-[#236840] text-white shadow-lg hover:shadow-xl hover:scale-[1.02]'
            : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
          }
        `}
        data-testid="continue-checkout-btn"
      >
        {buttonText}
      </Button>
      
      <p className="text-xs text-neutral-400 text-center">
        Skip or pause anytime. No commitment.
      </p>
    </div>
  );
}

// ============================================
// ADD-ON CARD - Modifier selection
// ============================================
function AddOnCard({ option, vendor, isSelected, onToggle }) {
  const fee = vendor?.modifier_fees?.[option.id] ?? option.fee ?? 0;
  const cleanLabel = option.label.replace(/\s*\(\+\$[\d.]+\)/, '').trim();
  
  return (
    <button
      onClick={onToggle}
      className={`
        relative p-4 rounded-xl text-left transition-all duration-200
        ${isSelected 
          ? 'bg-[#2a7d4f] text-white shadow-lg'
          : 'bg-white border border-neutral-100 hover:border-neutral-200 hover:shadow-md'
        }
      `}
    >
      {isSelected && (
        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-white flex items-center justify-center">
          <Check size={12} className="text-black" />
        </div>
      )}
      
      <div className={`font-medium ${isSelected ? 'text-white' : 'text-neutral-900'}`}>
        {cleanLabel}
      </div>
      <div className={`text-sm mt-1 ${isSelected ? 'text-white/70' : fee > 0 ? 'text-neutral-500' : 'text-green-600'}`}>
        {fee > 0 ? `+$${fee}/meal` : 'Free'}
      </div>
    </button>
  );
}

// ============================================
// MOBILE BOTTOM BAR - Sticky summary for mobile
// ============================================
function MobileBottomBar({ total, mealsSelected, mealsRequired, isValid, onCheckout }) {
  const isComplete = mealsSelected === mealsRequired;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-100 p-4 shadow-2xl md:hidden z-50">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs text-neutral-500">Weekly total</div>
          <div className="text-xl font-bold text-neutral-900">${total.toFixed(2)}</div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
          isComplete ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'
        }`}>
          {mealsSelected}/{mealsRequired} meals
        </div>
      </div>
      
      <Button
        onClick={onCheckout}
        disabled={!isValid}
        className={`
          w-full h-12 rounded-xl text-base font-semibold transition-all
          ${isValid 
            ? 'bg-[#2a7d4f] text-white'
            : 'bg-neutral-100 text-neutral-400'
          }
        `}
      >
        {isValid ? 'Continue to checkout' : `Add ${mealsRequired - mealsSelected} more`}
      </Button>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================
export default function WeeklySubscription({ vendor, menu, open, onOpenChange, user, mode = 'start', initialPlanId = null }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isPilot } = useAuth();
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [fulfillment, setFulfillment] = useState(isPilot ? "pickup" : "weekly");
  const [address, setAddress] = useState(user?.location || "");
  const [mods, setMods] = useState([]);
  const [quantities, setQuantities] = useState({});

  useEffect(() => { fetchPlans().then(setPlans); }, []);

  const vendorPlans = useMemo(() => {
    const byMeals = Object.fromEntries(plans.map(p => [p.meals, p]));
    const list = (vendor?.plans_offered || []).map(m => byMeals[m]).filter(Boolean);
    return list.sort((a, b) => a.meals - b.meals);
  }, [plans, vendor]);

  const popularPlanId = useMemo(() => {
    if (vendorPlans.length === 0) return null;
    const sevenMealPlan = vendorPlans.find(p => p.meals === 7);
    if (sevenMealPlan) return sevenMealPlan.id;
    const midIndex = Math.floor(vendorPlans.length / 2);
    return vendorPlans[midIndex]?.id || null;
  }, [vendorPlans]);

  useEffect(() => { 
    if (initialPlanId) { 
      setPlanId(initialPlanId); 
    } 
  }, [initialPlanId]);

  const perMealPrice = useMemo(() => planId ? vendor?.plan_prices?.[planId] : null, [vendor, planId]);
  const mealsRequired = useMemo(() => (plans.find(p => p.id === planId)?.meals || 0), [plans, planId]);
  const totalSelected = useMemo(() => Object.values(quantities).reduce((a, b) => a + Number(b || 0), 0), [quantities]);
  const modifiersPerMeal = useMemo(() => mods.reduce((sum, id) => sum + (vendor?.modifier_fees?.[id] || 0), 0), [mods, vendor]);
  const deliveryFee = fulfillment === 'weekly' ? (vendor?.delivery_fee || 0) : 0;

  const estimatedTotal = useMemo(() => {
    if (!perMealPrice) return 0;
    const base = perMealPrice * mealsRequired;
    const modsTotal = modifiersPerMeal * mealsRequired;
    return Math.round((base + modsTotal + (isPilot ? 0 : deliveryFee)) * 100) / 100;
  }, [perMealPrice, mealsRequired, modifiersPerMeal, deliveryFee, isPilot]);

  const cutoffPassed = useMemo(() => isAfterCutoff(vendor, address), [vendor, address]);
  const cutoffLabel = useMemo(() => formatCutoffDisplay(vendor, address), [vendor, address]);

  function inc(id) { setQuantities(q => ({ ...q, [id]: Number(q[id] || 0) + 1 })); }
  function dec(id) { setQuantities(q => ({ ...q, [id]: Math.max(0, Number(q[id] || 0) - 1) })); }
  function toggleMod(id) { setMods(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }

  const isValid = planId && totalSelected === mealsRequired;

  async function proceedToCheckout() {
    if (!planId) { toast({ title: "Choose a plan" }); return; }
    if (totalSelected !== mealsRequired) { 
      toast({ title: "Select meals", description: `Pick exactly ${mealsRequired} meals.` }); 
      return; 
    }
    
    const selections = Object.entries(quantities)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([menu_item_id, qty]) => ({ menu_item_id, qty: Number(qty) }));
    
    const payload = {
      vendorId: vendor.id,
      planId,
      meals: mealsRequired,
      selections,
      modifiers: mods,
      fulfillment,
      address,
    };
    
    setCheckoutDraft(payload);
    try { localStorage.setItem(`prepmate_selection_vendor_${vendor.id}`, '1'); } catch {}
    onOpenChange(false);
    navigate("/checkout");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden p-0 rounded-3xl border-0 shadow-2xl [&>button]:hidden">
        {/* Header - Fixed with safe padding */}
        <div className="sticky top-0 z-10 bg-white border-b border-neutral-100 pt-7 pb-4 px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-neutral-900 leading-tight">
                {mode === 'select' ? 'Choose your meals' : mode === 'modify' ? 'Modify your box' : 'Build your weekly box'}
              </h2>
              <p className="text-sm text-neutral-500 mt-1 truncate">{vendor?.name}</p>
            </div>
            <button 
              onClick={() => onOpenChange(false)}
              className="flex-shrink-0 w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X size={20} className="text-neutral-600" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 90px)' }}>
          <div className="flex flex-col md:flex-row">
            {/* Left Column - Plans & Meals */}
            <div className="flex-1 p-6 space-y-8 pb-32 md:pb-6">
              
              {/* Section A: Plan Selection */}
              {mode !== 'select' && (
                <section>
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
                    1. Choose your plan
                  </h3>
                  
                  {/* Mobile: Horizontal Scroll */}
                  <div className="flex gap-3 overflow-x-auto pb-4 -mx-6 px-6 md:hidden snap-x snap-mandatory scrollbar-hide">
                    {vendorPlans.map(p => (
                      <div key={p.id} className="snap-center">
                        <PlanCard
                          plan={p}
                          vendor={vendor}
                          isSelected={planId === p.id}
                          isPopular={p.id === popularPlanId}
                          onSelect={() => setPlanId(p.id)}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* Desktop: Grid - wider columns to prevent text truncation */}
                  <div className="hidden md:grid grid-cols-3 lg:grid-cols-6 gap-4">
                    {vendorPlans.map(p => (
                      <PlanCard
                        key={p.id}
                        plan={p}
                        vendor={vendor}
                        isSelected={planId === p.id}
                        isPopular={p.id === popularPlanId}
                        onSelect={() => setPlanId(p.id)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Section B: Meal Selection */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider">
                    {mode === 'select' ? 'Your meals' : '2. Pick your meals'}
                  </h3>
                </div>
                
                {/* Progress */}
                {mealsRequired > 0 && (
                  <div className="mb-6">
                    <SelectionProgress current={totalSelected} required={mealsRequired} />
                  </div>
                )}
                
                {/* Cutoff Warning */}
                {cutoffPassed && (
                  <div className="mb-4 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                    <p className="text-sm text-amber-800">
                      Selection cutoff passed. Changes apply to next week. Cutoff was {cutoffLabel}.
                    </p>
                  </div>
                )}
                
                {/* Meal Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menu.map(item => (
                    <MealCard
                      key={item.id}
                      item={item}
                      quantity={quantities[item.id] || 0}
                      onIncrease={() => inc(item.id)}
                      onDecrease={() => dec(item.id)}
                    />
                  ))}
                </div>
              </section>

              {/* Section: Add-ons */}
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider">
                    Add-ons
                  </h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info size={14} className="text-neutral-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        Applied to every meal in your box
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {CUSTOMIZATION_OPTIONS.map(opt => (
                    <AddOnCard
                      key={opt.id}
                      option={opt}
                      vendor={vendor}
                      isSelected={mods.includes(opt.id)}
                      onToggle={() => toggleMod(opt.id)}
                    />
                  ))}
                </div>
              </section>
            </div>

            {/* Right Column - Order Summary (Desktop) */}
            <div className="hidden md:block w-80 border-l border-neutral-100 p-6 bg-neutral-50/50">
              <div className="sticky top-6">
                <OrderSummary
                  planId={planId}
                  mealsRequired={mealsRequired}
                  totalSelected={totalSelected}
                  perMealPrice={perMealPrice}
                  modifiersPerMeal={modifiersPerMeal}
                  isPilot={isPilot}
                  vendor={vendor}
                  onCheckout={proceedToCheckout}
                  isValid={isValid}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Bar */}
        <MobileBottomBar
          total={estimatedTotal}
          mealsSelected={totalSelected}
          mealsRequired={mealsRequired}
          isValid={isValid}
          onCheckout={proceedToCheckout}
        />
      </DialogContent>
    </Dialog>
  );
}
